export const DEFAULT_REPORTING_COMPLETENESS_THRESHOLD = 0.95;

function toDate(value) {
  const date = value instanceof Date ? new Date(value.getTime()) : new Date(value);
  if (Number.isNaN(date.getTime())) {
    throw new TypeError("Reporting period requires a valid date");
  }
  return date;
}

function nonNegativeCount(value) {
  const count = Number(value ?? 0);
  return Number.isFinite(count) ? Math.max(0, count) : 0;
}

export function reportingMonthKey(value) {
  if (value == null) return null;
  const raw = value instanceof Date ? value.toISOString().slice(0, 10) : String(value);
  const match = raw.match(/^(\d{4}-\d{2})/);
  return match?.[1] ?? null;
}

export function formatReportingMonth(value, { long = false } = {}) {
  const monthKey = reportingMonthKey(value);
  if (!monthKey) return null;
  const date = new Date(`${monthKey}-01T00:00:00Z`);
  if (Number.isNaN(date.getTime())) return null;
  return new Intl.DateTimeFormat("en-US", {
    month: long ? "long" : "short",
    year: "numeric",
    timeZone: "UTC",
  }).format(date);
}

export function isReportingPeriodIncomplete(reportingPeriod) {
  return reportingPeriod != null && reportingPeriod.isComplete === false;
}

/**
 * Office display state only. Calendar finality never changes coverage
 * completeness or the resolver's canonical month/attendance selection.
 */
export function classifyAttendanceMonth(month, currentDate = new Date()) {
  if (month instanceof Date && Number.isNaN(month.getTime())) return "unknown";
  const key = reportingMonthKey(month);
  const date = new Date(`${key}-01T00:00:00Z`);
  const now = currentDate instanceof Date ? currentDate : new Date(currentDate);
  if (!key || Number.isNaN(date.getTime()) || reportingMonthKey(date) !== key
    || currentDate == null || Number.isNaN(now.getTime())) return "unknown";
  const currentKey = reportingMonthKey(now);
  return key < currentKey ? "elapsed" : key === currentKey ? "current" : "future";
}

// Office copy must use resolver metadata, not a locally reconstructed ratio.
export function overviewAttendanceCoveragePercent(reportingPeriod) {
  const raw = reportingPeriod?.coverageRatio;
  if (raw == null || raw === "") return null;
  const ratio = Number(raw);
  return Number.isFinite(ratio) && ratio >= 0 && ratio <= 1
    ? Math.round(ratio * 100)
    : null;
}

export function formatReportingPeriodCoverageTooltip(reportingPeriod) {
  const month = formatReportingMonth(reportingPeriod?.month, { long: true });
  if (!month) return null;

  const monthDate = new Date(`${reportingMonthKey(reportingPeriod.month)}-01T00:00:00Z`);
  monthDate.setUTCMonth(monthDate.getUTCMonth() + 1);
  const followingMonth = formatReportingMonth(monthDate, { long: true });
  const coverageRatio = reportingPeriod.coverageRatio != null
    ? Number(reportingPeriod.coverageRatio)
    : Number(reportingPeriod.eligibleOfficeDays) > 0
      ? Number(reportingPeriod.observedOfficeDays ?? 0) / Number(reportingPeriod.eligibleOfficeDays)
      : NaN;
  const coverage = Number.isFinite(coverageRatio)
    ? ` Coverage is ${Math.round(coverageRatio * 100)}%.`
    : "";

  return `Attendance for ${month} is still being finalized in ${followingMonth}.${coverage}`;
}

/**
 * Overview-only explanation: partial attendance coverage affects the peak
 * metrics, while the space, seats, and assigned-headcount figures are current.
 * Unlike the legacy copy, this percentage comes only from resolver metadata.
 */
export function formatOverviewAttendanceCoverageTooltip(reportingPeriod, currentDate = new Date()) {
  const month = formatReportingMonth(reportingPeriod?.month, { long: true });
  if (!month) return null;

  const state = classifyAttendanceMonth(reportingPeriod.month, currentDate);
  const percent = overviewAttendanceCoveragePercent(reportingPeriod);
  const coverage = percent != null ? ` at ${percent}% coverage of expected office-days` : "";
  const status = state === "elapsed"
    ? `attendance is final-as-measured${coverage}`
    : state === "current"
      ? `attendance is ${isReportingPeriodIncomplete(reportingPeriod) ? "finalizing" : "in progress"}${coverage}`
      : state === "future"
        ? "attendance period is upcoming"
        : "attendance timing is unknown";

  return `${month} ${status}. Missing observations remain missing; calendar finality does not make coverage complete. Attendance coverage affects Peak utilization and Peak count; sq ft, seats, and assigned headcount are current.`;
}

export function formatHomeAttendanceCoverageTooltip(reportingPeriod) {
  const month = formatReportingMonth(reportingPeriod?.month, { long: true });
  if (!month) return null;

  const monthDate = new Date(`${reportingMonthKey(reportingPeriod.month)}-01T00:00:00Z`);
  monthDate.setUTCMonth(monthDate.getUTCMonth() + 1);
  const followingMonth = formatReportingMonth(monthDate, { long: true });
  const ratio = reportingPeriod.coverageRatio != null
    ? Number(reportingPeriod.coverageRatio)
    : Number(reportingPeriod.eligibleOfficeDays) > 0
      ? Number(reportingPeriod.observedOfficeDays ?? 0) / Number(reportingPeriod.eligibleOfficeDays)
      : NaN;
  const coverage = Number.isFinite(ratio)
    ? ` Coverage is ${Math.round(ratio * 100)}% of expected office-days.`
    : "";

  return `Attendance for ${month} is still being finalized through ${followingMonth}. It updates automatically as attendance data arrives.${coverage} Badge-ins include employees and visitors until Envoy's employee/visitor split is available.`;
}

/**
 * Resolve the reporting month and aggregate completeness for Offices surfaces.
 * Coverage inputs must be pre-aggregated office-day and office counts.
 */
export function resolveReportingPeriod(
  currentDate = new Date(),
  coverage = {},
  completenessThreshold = DEFAULT_REPORTING_COMPLETENESS_THRESHOLD,
) {
  const date = toDate(currentDate);
  if (!Number.isFinite(completenessThreshold) || completenessThreshold < 0 || completenessThreshold > 1) {
    throw new RangeError("Reporting completeness threshold must be between 0 and 1");
  }

  const monthDate = new Date(Date.UTC(date.getUTCFullYear(), date.getUTCMonth() - 1, 1));
  const eligibleOfficeDays = nonNegativeCount(coverage.eligibleOfficeDays);
  const observedOfficeDays = Math.min(
    eligibleOfficeDays,
    nonNegativeCount(coverage.observedOfficeDays),
  );
  const totalOffices = nonNegativeCount(coverage.totalOffices);
  const officesReported = Math.min(totalOffices, nonNegativeCount(coverage.officesReported));
  const coverageRatio = eligibleOfficeDays > 0
    ? observedOfficeDays / eligibleOfficeDays
    : 0;

  return {
    month: monthDate.toISOString().slice(0, 10),
    observedOfficeDays,
    eligibleOfficeDays,
    officesReported,
    totalOffices,
    coverageRatio,
    completenessThreshold,
    isComplete: eligibleOfficeDays > 0 && coverageRatio >= completenessThreshold,
  };
}

/**
 * Choose the reporting-month row, falling back only to the newest row before
 * that target. When no resolver metadata is supplied, retain compatibility
 * with older test fixtures by selecting the newest available row.
 */
export function selectReportingMonthRow(rows, reportingPeriod) {
  if (!Array.isArray(rows) || rows.length === 0) return null;
  const targetKey = reportingMonthKey(reportingPeriod?.month);
  if (!targetKey) {
    const dated = rows
      .map((row) => ({ row, key: reportingMonthKey(row?.month) }))
      .filter(({ key }) => key)
      .sort((a, b) => a.key.localeCompare(b.key));
    if (dated.length > 0) {
      return dated.at(-1).row.peakDailyUniqueBadges != null
        ? dated.at(-1).row
        : [...dated].reverse().find(({ row }) => row.peakDailyUniqueBadges != null)?.row
          ?? dated.at(-1).row;
    }
    return [...rows].reverse().find((row) => row?.peakDailyUniqueBadges > 0)
      ?? [...rows].reverse().find((row) => row?.peakDailyUniqueBadges != null)
      ?? rows.at(-1);
  }
  const candidates = rows
    .map((row) => ({ row, key: reportingMonthKey(row?.month) }))
    .filter(({ key }) => key && key <= targetKey);
  if (candidates.length === 0) return null;

  const selectedKey = targetKey && candidates.some(({ key }) => key === targetKey)
    ? targetKey
    : candidates.reduce((latest, candidate) => candidate.key > latest ? candidate.key : latest, candidates[0].key);
  return candidates
    .filter(({ key }) => key === selectedKey)
    .reduce((best, candidate) => {
      if (!best) return candidate.row;
      const bestPeak = Number(best.peakDailyUniqueBadges ?? -Infinity);
      const candidatePeak = Number(candidate.row.peakDailyUniqueBadges ?? -Infinity);
      return candidatePeak > bestPeak ? candidate.row : best;
    }, null);
}

/**
 * Label the month actually represented, and make any lag behind the canonical
 * month explicit. An absent row returns the target label only when requested.
 */
export function formatReportingPeriodStatus(
  reportingPeriod,
  dataMonth,
  { emptyShowsTarget = false, longMonth = false } = {},
) {
  const targetKey = reportingMonthKey(reportingPeriod?.month);
  const dataKey = reportingMonthKey(dataMonth);
  const targetLabel = formatReportingMonth(reportingPeriod?.month, { long: longMonth });
  const dataLabel = formatReportingMonth(dataMonth, { long: longMonth });
  let label = null;
  if (dataKey && dataLabel) {
    label = targetKey && targetLabel && dataKey < targetKey
      ? `As of ${dataLabel} · ${targetLabel} pending`
      : `As of ${targetLabel ?? dataLabel}`;
  } else if (emptyShowsTarget && targetLabel) {
    label = targetLabel;
  }

  return label;
}